import { IAudienceSubscriptionsResponse, LogProvider, TrouterState } from "./Interfaces";
import { ITrouterRequest, ITrouterResponse, IMessageLossIndicator } from "./InternalInterfaces";
import { IManagerConsumer, ReconnectReason, TrouterConnection } from "./TrouterConnection";
import { State } from "./TrouterFsm";

/**
 * Intercepts calls from TrouterConnection to TrouterManager, to only allow
 * switching to the "connected" state after the connection
 * has been successfully registered, and disconnects Trouter if an existing
 * registration expires or gets revoked.
 * 
 * One instance of this wrappere exists per each TrouterConnection (though wrapping the same TrouterManger instance),
 * this object thus holds state specific to a particular TrouterConnection.
 */
export class RegistrationEnforcer implements IManagerConsumer {
    constructor(
        private wrapped: IManagerConsumer,
        private shouldConnectionDependOnRegistration: () => boolean,
        private shouldHoldBackEvents: () => boolean
    ) {
    }

    private isRegistered = false;

    private heldBackEvents: Array<HeldBackEvent> = [];

    onDownstreamRequest(connection: TrouterConnection, request: ITrouterRequest, response: ITrouterResponse): void {
        this.passIfRegisteredOrNotNeeded('onDownstreamRequest', connection, request, response);
    }

    onConnected(connection: TrouterConnection): void {
        this.passIfRegisteredOrNotNeeded('onConnected', connection);
    }

    onRegistered(connection: TrouterConnection): void {
        this.isRegistered = true;
        this.wrapped.onRegistered(connection);
        this.fireHeldBackEvents();
    }

    onUnregistered(connection: TrouterConnection): void {
        this.isRegistered = false;
        this.wrapped.onUnregistered(connection);
        if (connection.getState() === State.Connected && this.shouldConnectionDependOnRegistration()) {
            connection.forceReconnectDueToNoRegistration();
        }
    }

    onReconnecting(connection: TrouterConnection): void {
        this.wrapped.onReconnecting(connection);
    }

    onReconnectIsRequired(connection: TrouterConnection, useConnectParamsFromCache: boolean, reason: ReconnectReason): void {
        this.wrapped.onReconnectIsRequired(connection, useConnectParamsFromCache, reason);
    }

    onDisconnected(connection: TrouterConnection): void {
        this.isRegistered = false;
        this.dropHeldBackEvents();
        this.wrapped.onDisconnected(connection);
    }

    onTerminalError(connection: TrouterConnection): void {
        this.wrapped.onTerminalError(connection);
    }

    onConnectionParametersUpdated(connectParams: Record<string, unknown>): void {
        this.wrapped.onConnectionParametersUpdated(connectParams);
    }

    onTrouterMessageLost(flowTags: IMessageLossIndicator[]): void {
        this.passIfRegisteredOrNotNeeded('onTrouterMessageLost', flowTags);
    }

    onUserActivityStateAccepted(cv: string): void {
        this.passIfRegisteredOrNotNeeded('onUserActivityStateAccepted', cv);
    }

    onAudiencesSetResolved(audienceSubscriptionsResponse: IAudienceSubscriptionsResponse, cv: string): void {
        this.passIfRegisteredOrNotNeeded('onAudiencesSetResolved', audienceSubscriptionsResponse, cv);
    }

    getState(): TrouterState {
        return this.wrapped.getState();
    }

    private holdBackEvent<T extends HeldBackEventKind>(
        kind: T,
        ...args: Parameters<RegistrationEnforcer[T]>
    ) {
        this.heldBackEvents.push({ kind, args });
    }

    private passIfRegisteredOrNotNeeded<T extends HeldBackEventKind>(kind: T, ...args: Parameters<RegistrationEnforcer[T]>) {
        const connectionDependsOnRegistration = this.shouldConnectionDependOnRegistration();
        const holdBackEvents = this.shouldHoldBackEvents();
        // replay existing events if we previously needed registration but now don't, or if we should not hold back events anymore
        if (!connectionDependsOnRegistration || !holdBackEvents) {
            this.fireHeldBackEvents(
                connectionDependsOnRegistration ?
                    e => e.kind !== 'onConnected' :
                    undefined
            );
        }

        if (this.isRegistered || !connectionDependsOnRegistration || (holdBackEvents === false && kind !== 'onConnected')) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-return,@typescript-eslint/no-explicit-any
            (this.wrapped[kind] as any)(...args);
        } else {
            this.holdBackEvent(kind, ...args);
        }
    }

    private fireHeldBackEvents(filter?: (e: HeldBackEvent) => boolean){
        for (const event of this.heldBackEvents) {
            if (filter !== undefined && !filter(event)) {
                continue;
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-call,@typescript-eslint/no-unsafe-return,@typescript-eslint/no-explicit-any
            (this.wrapped[event.kind] as any)(...event.args);
        }
        
        if (filter === undefined) {
            this.heldBackEvents = [];
        } else {
            // keep everything that wasn't fired
            this.heldBackEvents = this.heldBackEvents.filter(e => !filter(e));
        }
    }

    private dropHeldBackEvents() {
        this.heldBackEvents = [];
    }
}

/**
 * Wrapper for debugging and tests, can be used to compare
 * the original and filtered events, by creating e.g.
 * ```
 * new LoggingManagerConsumer(
 *   new RegistrationEnforcer(
 *     new LoggingManagerConsumer(this, 'inner'),
 *     true
 *   ),
 * 'outer')
 * ```
 * where the outer one will log all the calls from TrouterConnection,
 * and the inner one will only log the calls that go through (with the
 * resulting order/delay/etc.)
 */
export class LoggingManagerConsumer implements IManagerConsumer {
    constructor(
        private wrapped: IManagerConsumer,
        private prefix: string,
        private logger?: LogProvider,
        private onEventAction?: (name: string, jsonArgs: string) => void
    ) {
    }

    private onEvent(name: string, jsonArgs: string) {
        if (this.onEventAction !== undefined) {
            this.onEventAction(name, jsonArgs);
        } else {
            (this.logger ?? console).log(`${this.prefix} TracingEnforcer: ${name}(${jsonArgs})`);    
        }
    }

    onDownstreamRequest(connection: TrouterConnection, request: ITrouterRequest, response: ITrouterResponse): void {
        this.onEvent('onDownstreamRequest', JSON.stringify({ request, response }));
        this.wrapped.onDownstreamRequest(connection, request, response);
    }

    onConnected(connection: TrouterConnection): void {
        this.onEvent('onConnected', connection.getState().toString());
        this.wrapped.onConnected(connection);
    }

    onRegistered(connection: TrouterConnection): void {
        this.onEvent('onRegistered', connection.getState().toString());
        this.wrapped.onRegistered(connection);
    }

    onUnregistered(connection: TrouterConnection): void {
        this.onEvent('onUnregistered', connection.getState().toString());
        this.wrapped.onUnregistered(connection);
    }

    onReconnecting(connection: TrouterConnection): void {
        this.onEvent('onReconnecting', connection.getState().toString());
        this.wrapped.onReconnecting(connection);
    }

    onReconnectIsRequired(connection: TrouterConnection, useConnectParamsFromCache: boolean, reason: ReconnectReason): void {
        this.onEvent('onReconnectIsRequired', JSON.stringify({ connection: connection.getState().toString(), useConnectParamsFromCache, reason }));
        this.wrapped.onReconnectIsRequired(connection, useConnectParamsFromCache, reason);
    }

    onDisconnected(connection: TrouterConnection): void {
        this.onEvent('onDisconnected', connection.getState().toString());
        this.wrapped.onDisconnected(connection);
    }

    onTerminalError(connection: TrouterConnection): void {
        this.onEvent('onTerminalError', JSON.stringify({ connection }));
        this.wrapped.onTerminalError(connection);
    }

    onConnectionParametersUpdated(connectParams: Record<string, unknown>): void {
        this.onEvent('onConnectionParametersUpdated', JSON.stringify({ connectParams }));
        this.wrapped.onConnectionParametersUpdated(connectParams);
    }

    onTrouterMessageLost(flowTags: IMessageLossIndicator[]): void {
        this.onEvent('onTrouterMessageLost', JSON.stringify({ flowTags }));
        this.wrapped.onTrouterMessageLost(flowTags);
    }

    onUserActivityStateAccepted(cv: string): void {
        this.onEvent('onUserActivityStateAccepted', JSON.stringify({ cv }));
        this.wrapped.onUserActivityStateAccepted(cv);
    }

    onAudiencesSetResolved(audienceSubscriptionsResponse: IAudienceSubscriptionsResponse, cv: string): void {
        this.onEvent('onAudiencesSetResolved', JSON.stringify({ audienceSubscriptionsResponse, cv }));
        this.wrapped.onAudiencesSetResolved(audienceSubscriptionsResponse, cv);
    }

    getState(): TrouterState {
        return this.wrapped.getState();
    }
}

// Enforces compile-time type safety of function arguments
type HeldBackEventKind =
    'onConnected' |
    'onDownstreamRequest' |
    'onTrouterMessageLost' |
    'onUserActivityStateAccepted' |
    'onAudiencesSetResolved';

type HeldBackEvent = {
    kind: HeldBackEventKind;
    args: Parameters<IManagerConsumer[HeldBackEventKind]>;
}



// WEBPACK FOOTER //
// ./lib_temp/RegistrationEnforcer.ts